import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import type { Installation, InstallationDetail } from '../shared/types'

export const useInstallations = (accountId?: string) =>
  useQuery({
    queryKey: ['installations', accountId ?? ''],
    queryFn: () => api.get<Installation[]>('/api/v1/installations', accountId ? { account_id: accountId } : undefined),
  })

export const useInstallation = (id: string) =>
  useQuery({
    queryKey: ['installations', id],
    queryFn: () => api.get<InstallationDetail>(`/api/v1/installations/${id}`),
    enabled: !!id,
  })

export function useCreateInstallation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { account_id: string; entitlement_id: string; name: string; environment: string; hosting: string }) =>
      api.post<{ id: string }>('/api/v1/installations', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['installations'] }),
  })
}

export function useUpdateInstallation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name?: string; environment?: string; hosting?: string }) => api.put(`/api/v1/installations/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['installations', id] })
      void qc.invalidateQueries({ queryKey: ['installations'] })
    },
  })
}

export const useBuildSpec = (id: string) =>
  useQuery({
    queryKey: ['installations', id, 'build-spec'],
    queryFn: () => api.get<Record<string, unknown>>(`/api/v1/installations/${id}/build-spec`),
    enabled: !!id,
  })

export const useCustomerServers = (accountId?: string) =>
  useQuery({
    queryKey: ['customer-servers', accountId ?? ''],
    queryFn: () => api.get<Record<string, unknown>[]>('/api/v1/customer-servers', accountId ? { account_id: accountId } : undefined),
  })

export function useCreateCustomerServer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { account_id: string; name: string; host?: string; ip_address?: string; panel_url?: string; vendor_id?: string; renewal_due?: string; payment_status?: string; access?: string }) =>
      api.post<{ id: string }>('/api/v1/customer-servers', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['customer-servers'] }),
  })
}

export function useCustomerServer(id: string) {
  return useQuery({
    queryKey: ['customer-servers', id],
    queryFn: () => api.get<Record<string, unknown>>(`/api/v1/customer-servers/${id}`),
    enabled: !!id,
  })
}

export function useRevealCustomerServer() {
  return useMutation({
    mutationFn: (id: string) => api.post<Record<string, unknown>>(`/api/v1/customer-servers/${id}/reveal`, {}),
  })
}

